import axios from 'axios';
import { format } from 'date-fns';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { Expense } from '../../models/expense.model';
import { CompanyExpenses } from '../../models/company.expenses.model';
import { authToken } from '../../scripts/authentication.script';
import { extractBase64 } from '../../scripts/extract.base.64.script';

export class ExpenseDA {
  static apiResponse: ApiResponse

  public static async getExpenses(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    treasury_id: string,
    validity: string,
    status: string,
    payment_method: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyExpenseList: CompanyExpenses | null = null
      let companyExpenseGroup: CompanyExpenses | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.expenses.list.data.json').then(response => { companyExpenseList = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/purchase.expenses.group.data.json').then(response => { companyExpenseGroup = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyExpenseList!.id_company === id_company || companyExpenseGroup!.id_company === id_company) {
        if (group_by.length === 0) {
          companyExpenseList!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, treasury: treasury_id, validity: validity, status: status, payment: payment_method, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        } else {
          companyExpenseGroup!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, treasury: treasury_id, validity: validity, status: status, payment: payment_method, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? companyExpenseList : companyExpenseGroup
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const params = new URLSearchParams()
      if (interval) params.append('interval', interval)
      if (start_date) params.append('start_date', start_date)
      if (end_date) params.append('end_date', end_date)
      if (contact_id) params.append('id_contact', contact_id)
      if (treasury_id) params.append('treasury_account', treasury_id)
      if (validity === 'valid') params.append('validated', 'true')
      else if (validity === 'invalid') params.append('validated', 'false')
      if (status) params.append('status', status)
      if (payment_method) params.append('payment_method', payment_method)
      if (sort_field) params.append('sort_by', sort_field)
      if (sort_mode) params.append('sort_order', sort_mode)
      if (group_by) params.append('group_by', group_by)
      if (search) params.append('search', search)
      if (all) {
        params.append('all', 'true')
      } else {
        params.append('page', String(page))
        params.append('limit', String(limit))
      }
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getExpense(
    id_company: string,
    id_expense: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let expense: Expense | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.expense.data.json').then(response => { expense = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (expense!.id === id_expense) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: expense
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postExpense(
    id_company: string,
    provider: any,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string }[] }[],
    vouchers: { name: string, file: any, id_document_inbox?: string }[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append("provider", provider ? JSON.stringify(provider) : '')
      data.append('concerning', concerning)
      data.append('created_date', date)
      data.append('treasury_account', treasury_account ? JSON.stringify(treasury_account) : '')
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')
      data.append('company', JSON.stringify(company_detail))

      requirements.forEach(item => {
        data.append('requirements', JSON.stringify(item))
      })

      vouchers.forEach(item => {
        if (item.file instanceof File) {
          data.append('vouchers', item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postExpenseDraft(
    id_company: string,
    vouchers: { name: string, file: any, id_document_inbox?: string }[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let expense: Expense | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/purchase.expense.draft.data.json').then(response => { expense = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        let listVouchers = await Promise.all(
          vouchers.map(async item => {
            let file = await extractBase64(item.file) as { base: string }
            return {
              id: `voucher${(Math.floor(Math.random() * 1000)).toString()}`,
              name: item.name,
              size: item.name.length * 100,
              created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
              file: file.base
            }
          })
        )

        expense!.vouchers = listVouchers

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: expense
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/draft`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData();
      data.append('company', JSON.stringify(company_detail))

      vouchers.forEach(item => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putExpense(
    id_company: string,
    id_expense: string,
    provider: any,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string }[] }[],
    vouchers: { name: string, file: any, id_document_inbox?: string }[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let expense: Expense | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.expense.data.json').then(response => { expense = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (expense!.id === id_expense) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append("provider", provider ? JSON.stringify(provider) : '')
      data.append('concerning', concerning)
      data.append('created_date', date)
      data.append('treasury_account', treasury_account ? JSON.stringify(treasury_account) : '')
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')

      requirements.forEach(item => {
        data.append('requirements', JSON.stringify(item))
      })

      vouchers.forEach(item => {
        if (item.file instanceof File) {
          data.append('vouchers', item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putExpenseStatus(
    id_company: string,
    id_invoice: string,
    amount: number | null,
    _company_tax: { subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string } | null,
    content_accounts: { id_content: string, id_account_credit: string, id_account_debit: string }[] | null,
    effective_accounts: { id_content: string, id_account_tax: string }[] | null,
    treasury_account?: string | null,
    bank_amount?: number | null
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let expense: Expense | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.expense.data.json').then(response => { expense = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (expense!.id === id_invoice) {
        expense!.validated = (expense!.validated) ? false : true
        expense!.status = (expense!.validated) ? 'completed' : 'in-progress'

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: expense
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_invoice}/status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        amount: amount,
        content_accounts: content_accounts,
        effective_accounts: effective_accounts,
        treasury_account: treasury_account ?? undefined,
        bank_amount: bank_amount ?? undefined
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteExpense(
    id_company: string,
    id_expense: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let expenses: Expense[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.expenses.list.data.json').then(response => { expenses = response.data.expenses }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (expenses!.some(item => id_expense === item.id)) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async revertToDraft(id_company: string, id_expense: string): Promise<ApiResponse> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}/revert-draft`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.put(url, {}, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async attachVoucher(id_company: string, id_expense: string, body: { id_document_inbox: string, role?: string }): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async uploadVoucher(id_company: string, id_expense: string, file: File, role?: string): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      let config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteVoucher(id_company: string, id_expense: string, index: number): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}/vouchers/${index}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async postEmployeeExpense(
    id_company: string,
    id_contact: string,
    concerning: string,
    created_date: string,
    currency: any,
    foreign_currency: any,
    company_detail: any,
    requirements: { type: string, description: string, contents: any[] }[] | { type: string, description: string, contents: any[] },
    voucher_files: { name: string, file: File, id_document_inbox?: string }[],
    provider?: any
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses`
      const config = { headers: { Authorization: authToken() } }

      const data = new FormData()
      data.append('paid_by', 'employee')
      data.append('id_contact', id_contact)
      data.append('provider', provider ? JSON.stringify(provider) : '')
      data.append('treasury_account', '')
      data.append('concerning', concerning)
      data.append('created_date', created_date)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')
      data.append('company', JSON.stringify(company_detail))
      data.append('source', 'web')

      const reqArray = Array.isArray(requirements) ? requirements : [requirements]
      reqArray.forEach(item => {
        data.append('requirements', JSON.stringify(item))
      })

      voucher_files.forEach(item => {
        if (item.file instanceof File) {
          data.append('vouchers', item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      const response = await axios.post(url, data, config)
      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async patchAccounts(id_company: string, id_expense: string, content_accounts: {id_content: string, id_account_debit: string, id_account_credit: string}[], effective_accounts?: {id_content: string, id_account_tax: string}[] | null, company?: {subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string} | null): Promise<ApiResponse> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expenses/${id_expense}/accounts`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.patch(url, { content_accounts, effective_accounts: effective_accounts ?? undefined, company: company ?? undefined }, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }
}
